import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { getFCMToken } from "@/lib/firebase";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("mmep_token");
    if (!token) { setLoading(false); return; }
    try {
      let data;
      try {
        ({ data } = await api.get("/auth/me"));
      } catch (normalAuthError) {
        const employeeResponse = await api.get("/employee/auth/me");
        data = employeeResponse.data;
      }
      setUser(data);
    } catch (e) {
      localStorage.removeItem("mmep_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFavorites = useCallback(async () => {
    if (!localStorage.getItem("mmep_token")) return;
    try {
      const { data } = await api.get("/favorites");
      setFavorites(data.vendor_ids || []);
    } catch (e) {}
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { if (user) loadFavorites(); }, [user, loadFavorites]);

  const login = (token, u) => {
    localStorage.setItem("mmep_token", token);
    setUser(u);
    setAuthOpen(false);

    // Register FCM automatically when browser notification permission
    // has already been granted. Do not trigger a permission popup here.
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "granted") {
        enablePushNotifications().catch(() => {});
      }
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("mmep_token");
    setUser(null);
    setFavorites([]);
  };

  const refreshUser = async () => { try { const { data } = await api.get("/auth/me"); setUser(data); } catch (e) {} };

  const enablePushNotifications = useCallback(async () => {
    const token = await getFCMToken();

    if (!token) {
      return { ok: false, reason: "permission_denied" };
    }

    await api.post("/auth/fcm-token", {
      token,
      platform: "web",
    });

    return { ok: true };
  }, []);

  const toggleFavorite = async (vendorId) => {
    if (!user) { setAuthOpen(true); return; }
    const { data } = await api.post("/favorites/toggle", { vendor_id: vendorId });
    setFavorites((prev) => data.favorited ? [...prev, vendorId] : prev.filter((id) => id !== vendorId));
    return data.favorited;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, authOpen, setAuthOpen, favorites, toggleFavorite, loadFavorites, enablePushNotifications }}>
      {children}
    </AuthContext.Provider>
  );
};
